import React from "react";
import { useTranslation } from "react-i18next";
import { Column, Row, ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui";
import { LiveEvent, LiveEventTypeEnum } from "@/__generated__/types";
import { useLocale } from "@/hooks/useLocale";
import { formatDate } from "@/lib/formatDate";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { timestampToDate } from "@/util/dateUtils";

const DATE_FORMAT = "PPPp";
const PLACEHOLDER_FALLBACK = "--";

export const useCompletedEventsColumns = (): ColumnDef<LiveEvent>[] => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { navToRoute } = useProtectedNavigation();

  const getEventTypeLabel = (eventType: string) => {
    switch (eventType) {
      case LiveEventTypeEnum.LiveEvent:
        return t("gamesAndLiveEvents.table.eventTypes.liveEvent");
      case LiveEventTypeEnum.BotEvent:
        return t("gamesAndLiveEvents.table.eventTypes.automatedGame");
      default:
        return eventType;
    }
  };

  const columns: ColumnDef<LiveEvent>[] = [
    {
      id: "name",
      accessorFn: (row: LiveEvent) => row?.name || "",
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesAndLiveEvents.table.completed.columns.eventName")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          <div className="w-80 overflow-hidden text-ellipsis">
            {row.original?.name || PLACEHOLDER_FALLBACK}
          </div>
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "startsAt",
      accessorFn: (row: LiveEvent) => {
        const { startsAt } = row || {};
        return startsAt
          ? formatDate(timestampToDate(startsAt * 1000), DATE_FORMAT, {
              locale: locale as "en",
            })
          : "";
      },
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesAndLiveEvents.table.completed.columns.startTime")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => {
        const { startsAt } = row.original || {};
        return (
          <DataTable.TBodyCell>
            {startsAt
              ? formatDate(timestampToDate(startsAt * 1000), DATE_FORMAT, {
                  locale: locale as "en",
                })
              : PLACEHOLDER_FALLBACK}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "participants",
      accessorFn: (row: LiveEvent) => row?.statistics?.totalParticipants || 0,
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesAndLiveEvents.table.completed.columns.participants")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => {
        const event = row.original;
        if (!event) {
          return (
            <DataTable.TBodyCell>{PLACEHOLDER_FALLBACK}</DataTable.TBodyCell>
          );
        }

        const totalParticipants = event.statistics?.totalParticipants || 0;

        return (
          <DataTable.TBodyCell>
            <div className="flex items-center gap-2">
              <span className="text-sm">
                {totalParticipants.toLocaleString()}
              </span>
            </div>
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "totalMessages",
      accessorFn: (row: LiveEvent) => row?.statistics?.totalMessages || 0,
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesAndLiveEvents.table.completed.columns.totalMessages")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          {row.original?.statistics?.totalMessages || 0}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "eventType",
      accessorFn: (row: LiveEvent) => getEventTypeLabel(row?.eventType || ""),
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesAndLiveEvents.table.completed.columns.eventType")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          {row.original
            ? getEventTypeLabel(row.original.eventType)
            : PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "chatChannel",
      accessorFn: (row: LiveEvent) => row?.chatChannel?.displayName || "",
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesAndLiveEvents.table.completed.columns.chatChannel")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          {row.original?.chatChannel?.displayName || PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "actions",
      enableSorting: false,
      enableColumnFilter: false,
      header: () => <DataTable.THeaderCell>{""}</DataTable.THeaderCell>,
      cell: ({ row }: { row: Row<LiveEvent> }) => {
        const event = row.original;
        if (!event) {
          return <DataTable.TBodyCell>{""}</DataTable.TBodyCell>;
        }

        return (
          <DataTable.TBodyCell isLastCell>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  navToRoute("eventResults", { eventId: event.id })
                }
              >
                {t("gamesAndLiveEvents.table.completed.actions.details")}
              </Button>
            </div>
          </DataTable.TBodyCell>
        );
      },
    },
  ];

  return columns;
};
